import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Draggable from 'react-draggable';
import { useSelector } from 'react-redux';
import socketClient, { Socket } from 'socket.io-client';

import MicIcon from '@material-ui/icons/Mic';
import { Switch } from '@material-ui/core';
import CallIcon from '@material-ui/icons/Call';
import CloseIcon from '@material-ui/icons/Close';
import MicOffIcon from '@material-ui/icons/MicOff';
import CallEndIcon from '@material-ui/icons/CallEnd';
import VideocamIcon from '@material-ui/icons/Videocam';
import VideocamOffIcon from '@material-ui/icons/VideocamOff';
import ScreenShareIcon from '@material-ui/icons/ScreenShare';
import StopScreenShareIcon from '@material-ui/icons/StopScreenShare';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import FullscreenExitOutlinedIcon from '@material-ui/icons/FullscreenExitOutlined';
import FullscreenOutlinedIcon from '@material-ui/icons/FullscreenOutlined';

import EnvVars from 'helpers/envVars';

import useProduct from 'hooks/useProduct';
import { usePermissions } from 'hooks/usePermissions';

import proposalServices from 'services/proposalServices';

import { ReactComponent as HeadsetIcon } from './headset-icon.svg';

import {
  AcceptButton,
  CallCard,
  CameraControlButton,
  CameraControls,
  ErrorMessage,
  LocalVideo,
  LocalVideoMini,
  RejectButton,
  Video,
  VideoContainer,
  VideoLabel,
  VideoLabelMini,
  VideoRoomContainer,
  VideoWrapper,
  StatusContainer,
  StatusCard,
  StatusContent,
  StatusInfo,
  StatusIcon,
  StatusText,
  CallListContainer,
  DealLink,
  CallActions,
  CallInfo,
  CallUserName,
  CallDealContainer,
} from './styles';

const VideoCallContainer = styled.div<{ minimized: boolean }>`
  ${({ minimized }) =>
    minimized
      ? `
    position: fixed;
    bottom: 32px;
    right: 32px;
    width: 340px;
    height: 220px;
    border-radius: 16px;
    z-index: 3000;
    padding: 12px 12px 8px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    transition: box-shadow 0.2s;
  `
      : `
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.4);
    z-index: 3000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  `}
`;

const VideoCallContent = styled.div<{ minimized: boolean }>`
  ${({ minimized }) =>
    minimized
      ? `
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
  `
      : `
    position: relative;
    border-radius: 16px;
    width: 90vw;
    height: 90vh;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    background: #000;
    overflow: hidden;
  `}
`;

const MinimizeBtn = styled.button`
  display: flex;
  position: absolute;
  top: 32px;
  right: 32px;
  background: #fff;
  border: none;
  cursor: pointer;
  z-index: 10;
  padding: 4px;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
`;

const RestoreBtn = styled.button`
  display: flex;
  position: absolute;
  top: 8px;
  right: 8px;
  background: #fff;
  border: none;
  cursor: pointer;
  z-index: 10;
  padding: 4px;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;

  &:hover {
    opacity: 0.8;
  }
`;

const socketDomain = EnvVars('SOCKET_VIDEO_CALL_API');

const addNewTag = async (proposalId, dealerId, newTag) => {
  try {
    const response: any = await proposalServices.addTag(dealerId, proposalId, [
      newTag,
    ]);
    if (response.success) {
      // callbackOnSuccess(checkedTags);
      // dispatch(
      //   UIActionCreators.snackbarManagement(
      //     'success',
      //     'Tag adicionada com sucesso!'
      //   )
      // );
    }
  } catch {
    // dispatch(
    //   UIActionCreators.snackbarManagement(
    //     'error',
    //     'Falha ao adicionar uma nova tag.'
    //   )
    // );
  }
};

const VideoCallProvider = ({ hiddeMenuAndHeaderItens }) => {
  const { hasProduct } = useProduct();
  const { hasPermission } = usePermissions();

  const [isOpenCallDialog, setIsOpenCallDialog] = useState<boolean>(false);

  const [socket, setSocket] = useState<Socket | null>(null);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [callList, setCallList] = useState([]);
  const [remoteUserId, setRemoteUserId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentCallData, setCurrentCallData] = useState(null);
  const [buyerInfos, setBuyerInfo] = useState(null);

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const pendingCandidates = useRef<RTCIceCandidate[]>([]);
  const localStreamRef = useRef<MediaStream | null>(null);

  const { currentDealer } = useSelector(state => state.dealerStore);
  const userStore = useSelector(state => state.userStore);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);

  const [isMinimized, setIsMinimized] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [showError, setShowError] = useState(true);
  const [isStatusMinimized, setIsStatusMinimized] = useState(false);
  const [showCallList, setShowCallList] = useState(true);

  const cleanup = () => {
    console.log('Limpando recursos da chamada...');

    // Parar todas as tracks locais
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        console.log('Parando track local:', {
          kind: track.kind,
          enabled: track.enabled,
          muted: track.muted,
          readyState: track.readyState,
        });
        track.stop();
      });
      localStreamRef.current = null;
    }

    // Fechar a conexão peer
    if (peerConnection.current) {
      console.log('Fechando conexão peer...');
      peerConnection.current.close();
      peerConnection.current = null;
    }

    // Limpar o vídeo remoto
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
  };

  const addPendingCandidates = async () => {
    if (!peerConnection.current) {
      console.log('Nenhuma conexão peer disponível para adicionar candidatos');
      return;
    }

    console.log(
      'Tentando adicionar candidatos pendentes:',
      pendingCandidates.current.length
    );
    while (pendingCandidates.current.length > 0) {
      const candidate = pendingCandidates.current.shift();
      if (candidate) {
        try {
          await peerConnection.current.addIceCandidate(candidate);
          console.log('Candidato ICE adicionado com sucesso');
        } catch (error) {
          console.error('Erro ao adicionar candidato ICE:', error);
          pendingCandidates.current.unshift(candidate);
          break;
        }
      }
    }
  };

  const initializeMedia = async () => {
    try {
      console.log('Solicitando acesso à mídia...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      const tracks = stream.getTracks();
      console.log('Mídia local obtida com sucesso');
      console.log(
        'Tracks disponíveis:',
        tracks.map(track => ({
          kind: track.kind,
          enabled: track.enabled,
          muted: track.muted,
          readyState: track.readyState,
        }))
      );

      // Primeiro definir o stream no elemento de vídeo
      if (localVideoRef.current) {
        console.log('Definindo stream local no elemento de vídeo');
        localVideoRef.current.srcObject = stream;
      }

      // Armazenar o stream na referência
      console.log('Armazenando stream local na referência');
      localStreamRef.current = stream;
    } catch (error) {
      console.error('Erro ao acessar mídia:', error);
      setError(
        'Erro ao acessar câmera e microfone. Por favor, verifique as permissões do navegador.'
      );
    }
  };

  const createPeerConnection = roomId => {
    console.log('Criando nova conexão peer');
    let pc = null;

    if (
      !peerConnection.current ||
      peerConnection.current.signalingState === 'closed'
    ) {
      pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          { urls: 'stun:stun3.l.google.com:19302' },
          { urls: 'stun:stun4.l.google.com:19302' },
          {
            urls: 'stun:global.stun.twilio.com:3478',
          },
          {
            credential: '5NaKgMJU+0VbQ+hYi+AAOrIH7Xb90FVegb/ceMPFAh0=',
            urls: 'turn:global.turn.twilio.com:3478?transport=udp',
            username:
              '10283481af659a716dfd98ca1ffaca0c583524460d823bf4eafef96e2670a4c8',
          },
          {
            credential: '5NaKgMJU+0VbQ+hYi+AAOrIH7Xb90FVegb/ceMPFAh0=',
            urls: 'turn:global.turn.twilio.com:3478?transport=tcp',
            username:
              '10283481af659a716dfd98ca1ffaca0c583524460d823bf4eafef96e2670a4c8',
          },
          {
            credential: '5NaKgMJU+0VbQ+hYi+AAOrIH7Xb90FVegb/ceMPFAh0=',
            urls: 'turn:global.turn.twilio.com:443?transport=tcp',
            username:
              '10283481af659a716dfd98ca1ffaca0c583524460d823bf4eafef96e2670a4c8',
          },
        ],
        iceCandidatePoolSize: 10,
        iceTransportPolicy: 'all',
        bundlePolicy: 'max-bundle',
        rtcpMuxPolicy: 'require',
      });

      // Atribuir a nova conexão ao peerConnection.current
      peerConnection.current = pc;
    } else {
      // Se já existe uma conexão válida, usar ela
      pc = peerConnection.current;
    }

    if (localStreamRef.current) {
      console.log('Adicionando tracks locais à conexão peer');
      localStreamRef.current.getTracks().forEach(track => {
        console.log('Adicionando track local:', {
          kind: track.kind,
          enabled: track.enabled,
          muted: track.muted,
          readyState: track.readyState,
        });
        pc.addTrack(track, localStreamRef.current!);
      });
    } else {
      console.warn(
        'Nenhum stream local disponível para adicionar à conexão peer'
      );
    }

    pc.ontrack = event => {
      console.log('Evento ontrack recebido');
      console.log('Track recebida:', {
        kind: event.track.kind,
        enabled: event.track.enabled,
        muted: event.track.muted,
        readyState: event.track.readyState,
      });
      console.log('Streams disponíveis:', event.streams.length);

      if (event.streams && event.streams[0]) {
        const remoteStream = event.streams[0];
        console.log(
          'Tracks no stream remoto:',
          remoteStream.getTracks().map(track => ({
            kind: track.kind,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
          }))
        );

        if (remoteVideoRef.current) {
          console.log('Definindo stream remoto no elemento de vídeo');
          remoteVideoRef.current.srcObject = remoteStream;
        }
      } else {
        console.error('Nenhum stream disponível no evento ontrack');
      }
    };

    pc.onicecandidate = event => {
      if (event.candidate && socket) {
        console.log('Candidato ICE gerado:', {
          type: event.candidate.type,
          protocol: event.candidate.protocol,
          address: event.candidate.address,
          port: event.candidate.port,
        });

        // Adiciona timeout para candidatos ICE
        const timeout = setTimeout(() => {
          console.warn('Timeout ao enviar candidato ICE');
        }, 5000);

        socket?.emit('ice-candidate', event.candidate, roomId, () => {
          clearTimeout(timeout);
          console.log('Candidato ICE enviado com sucesso');
        });
      }
    };

    // Adicionar listeners para debug de conexão
    pc.oniceconnectionstatechange = () => {
      console.log('ICE Connection State:', pc.iceConnectionState);

      if (pc.iceConnectionState === 'failed') {
        console.error('Conexão ICE falhou - tentando reiniciar...');

        setError('Problema de conexão detectado. Tentando reconectar...');

        // Tenta reconectar automaticamente
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        retryConnection();
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('Connection State:', pc.connectionState);

      if (pc.connectionState === 'connected') {
        console.log('Conexão peer estabelecida com sucesso!');

        setError(null);
      } else if (pc.connectionState === 'failed') {
        console.error('Conexão peer falhou');

        setError('Falha na conexão. Verifique sua rede e tente novamente.');
      } else if (pc.connectionState === 'disconnected') {
        console.warn('Conexão peer desconectada');

        setError('Conexão perdida. Tentando reconectar...');
      }
    };

    pc.onicegatheringstatechange = () => {
      console.log('Estado de coleta ICE:', pc.iceGatheringState);
    };

    pc.onsignalingstatechange = () => {
      console.log('Estado de sinalização:', pc.signalingState);
    };

    return pc;
  };

  const playNotificationSound = async () => {
    if (!audioRef.current || !audioContext) return;

    try {
      // Verificar se o áudio está pronto
      if (audioRef.current.readyState < 2) {
        console.log('Aguardando carregamento do áudio...');
        await new Promise(resolve => {
          audioRef.current!.addEventListener('canplaythrough', resolve, {
            once: true,
          });
        });
      }

      // Se o contexto estiver suspenso, tenta retomar
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // Tenta reproduzir o som
      await audioRef.current.play();
      console.log('Som iniciado com sucesso');
    } catch (error) {
      console.error('Erro ao tocar o som:', error);
      // Tenta uma abordagem alternativa
      try {
        const source = audioContext.createMediaElementSource(audioRef.current);
        source.connect(audioContext.destination);
        await audioRef.current.play();
        console.log('Som iniciado com sucesso (método alternativo)');
      } catch (fallbackError) {
        console.error(
          'Erro ao tocar o som (método alternativo):',
          fallbackError
        );
      }
    }
  };

  const rejectCall = (callData, insideCall = false) => {
    if (insideCall) {
      socket.emit('seller-reject-call', callData);
    }
    if (!insideCall) {
      setCallList(callList.filter(call => call.roomId !== callData.roomId));
      if (callList.length <= 1 && audioRef.current) {
        console.log('Parando som após rejeição...');
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  };

  const endCall = (skipRejectCall = false) => {
    console.log('Encerrando chamada...');
    if (!skipRejectCall) {
      rejectCall(currentCallData, true);
    }
    //VIDEO CHAMADA FINALIZADA
    addNewTag(currentCallData?.proposalId, currentCallData?.dealerId, 125500);
    cleanup();
    setRemoteUserId(null);
    setIsOpenCallDialog(false);

    // Resetando estados dos botões
    setIsCameraOn(true);
    setIsMicOn(true);
    setIsScreenSharing(false);

    // Limpando o erro e seu estado de exibição
    setError(null);
    setShowError(true);
  };

  const startCall = async (callData, socket) => {
    try {
      //VIDEO CHAMADA ATENDIDA
      addNewTag(callData?.proposalId, callData?.dealerId, 125504);
      setCurrentCallData(callData);
      setIsOpenCallDialog(true);
      setShowError(true);

      // Limpar todas as chamadas pendentes do callList e parar o som
      setCallList([]);
      if (audioRef.current) {
        console.log('Parando som ao aceitar chamada...');
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }

      console.log('Iniciando chamada...');

      // Limpar qualquer estado anterior antes de iniciar nova chamada
      cleanup();

      // Primeiro, inicializar a mídia
      await initializeMedia();

      // Verificar se temos o stream local
      if (!localStreamRef.current) {
        throw new Error('Não foi possível obter o stream local');
      }

      // Criar a conexão peer após ter o stream local
      peerConnection.current = createPeerConnection(callData.roomId);

      socket?.emit('seller-join-call-room', callData.roomId);
    } catch (error) {
      console.error('Erro ao iniciar chamada:', error);
      setError('Erro ao iniciar a chamada. Tente novamente.');
    }
  };

  const toggleMic = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMicOn(!isMicOn);
      } else {
        console.log('Nenhum track de áudio disponível no momento');
      }
    }
  };

  const toggleScreenShare = async () => {
    try {
      if (!isScreenSharing) {
        // Salvando o stream de áudio atual antes de compartilhar a tela
        const currentAudioTrack = localStreamRef.current?.getAudioTracks()[0];

        const screenStream = await (navigator.mediaDevices as any).getDisplayMedia(
          {
            video: true,
          }
        );

        if (localStreamRef.current) {
          localStreamRef.current
            .getVideoTracks()
            .forEach(track => track.stop());
        }

        // Combinando o stream de vídeo da tela com o áudio existente
        if (currentAudioTrack) {
          screenStream.addTrack(currentAudioTrack);
        }

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = screenStream;
        }

        localStreamRef.current = screenStream;

        if (peerConnection.current) {
          const videoTrack = screenStream.getVideoTracks()[0];
          const sender = peerConnection.current
            .getSenders()
            .find(s => s.track?.kind === 'video');

          if (sender) {
            sender.replaceTrack(videoTrack);
          }
        }
      } else {
        // Voltando para a câmera
        const cameraStream = await (navigator.mediaDevices as any).getUserMedia(
          {
            video: true,
            audio: true,
          }
        );

        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach(track => track.stop());
        }

        // Aplicando o estado do microfone ao novo stream
        const audioTrack = cameraStream.getAudioTracks()[0];
        if (audioTrack) {
          audioTrack.enabled = isMicOn;
        }

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = cameraStream;
        }

        localStreamRef.current = cameraStream;

        if (peerConnection.current) {
          // Atualizando tanto o vídeo quanto o áudio no peer connection
          const videoTrack = cameraStream.getVideoTracks()[0];
          const audioTrack = cameraStream.getAudioTracks()[0];

          const videoSender = peerConnection.current
            .getSenders()
            .find(s => s.track?.kind === 'video');
          const audioSender = peerConnection.current
            .getSenders()
            .find(s => s.track?.kind === 'audio');

          if (videoSender) {
            videoSender.replaceTrack(videoTrack);
          }
          if (audioSender && audioTrack) {
            audioSender.replaceTrack(audioTrack);
          }
        }
      }

      setIsScreenSharing(!isScreenSharing);
    } catch (error) {
      console.error('Erro ao compartilhar tela:', error);
      setError('Erro ao compartilhar tela. Tente novamente.');
    }
  };

  const toggleCamera = async () => {
    if (localStreamRef.current) {
      if (isScreenSharing && !isCameraOn) {
        // Se estiver compartilhando tela e a câmera estiver desativada, desativa o compartilhamento
        await toggleScreenShare();
      } else {
        const videoTrack = localStreamRef.current.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.enabled = !videoTrack.enabled;
        }
      }
      setIsCameraOn(!isCameraOn);
    }
  };

  const handleRestore = () => {
    setIsMinimized(false);
    setDragPosition({ x: 0, y: 0 });
  };

  const handleCloseError = () => {
    setShowError(false);
    setError(null);
  };

  const retryConnection = async () => {
    console.log('Tentando reconectar...');
    cleanup();
    await new Promise(resolve => setTimeout(resolve, 1000));

    try {
      await initializeMedia();
      peerConnection.current = createPeerConnection(currentCallData.roomId);
      console.log('Reconexão iniciada');
    } catch (error) {
      console.error('Erro na reconexão:', error);
    }
  };

  // Resetar showError quando um novo erro for definido
  useEffect(() => {
    if (error) {
      setShowError(true);
    }
  }, [error]);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  useEffect(() => {
    if (localStreamRef.current && !isInitialized) {
      setIsInitialized(true);
    }
  }, [localStreamRef.current, isInitialized]);

  useEffect(() => {
    // Criando o elemento de áudio com o caminho correto
    audioRef.current = new Audio();
    audioRef.current.src = '/video-call-sound.mp3';
    audioRef.current.loop = true;
    audioRef.current.volume = 1;

    // Criar AudioContext
    const context = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    setAudioContext(context);

    // Verificar se o áudio foi carregado
    audioRef.current.addEventListener('loadeddata', () => {
      console.log('Áudio carregado com sucesso');
    });

    audioRef.current.addEventListener('error', e => {
      console.error('Erro ao carregar áudio:', e);
    });

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (context) {
        context.close();
      }
    };
  }, []);

  useEffect(() => {
    if (!audioRef.current) return;

    if (callList.length > 0) {
      console.log('Iniciando som de chamada...');
      playNotificationSound();
    } else {
      console.log('Parando som de chamada...');
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [callList]);

  const toggleConnection = action => {
    switch (action) {
      case 'connect': {
        // Sempre criar uma nova conexão e registrar listeners
        const currentSocket = socketClient(socketDomain, {
          path: '/socket',
          transports: ['websocket'],
          reconnectionDelay: 500,
        });

        setSocket(currentSocket);

        currentSocket.on('connect', () => {
          console.log('Conectado ao servidor Socket.IO');
          setError(null);
          setConnected(true);

          currentSocket.emit('register-seller', {
            mobiUserId: Number(userStore?.userSite?.id) || 0,
            email: userStore?.email || '',
            username: userStore?.username || '',
            firstName: userStore?.firstName || '',
            dealerId: Number(currentDealer?.id) || 0,
          });
        });

        currentSocket.on('buyer-request-call', data => {
          console.log('Solicitação de chamada recebida:', data);

          //VIDEO CHAMADA INICIADA
          addNewTag(data?.proposalId, data?.dealerId, 125503);

          setCallList(prevState => [...prevState, data]);
        });

        currentSocket.on('buyer-reject-call', roomId => {
          console.log('Chamada rejeitada:', roomId);

          endCall(true);

          setCallList(prevState =>
            prevState.filter(call => call.roomId !== roomId)
          );
        });

        currentSocket.on('call-attended', ({ roomId }) => {
          console.log('Chamada atendida:', roomId, 'ja foi atendida');

          setCallList(prevState =>
            prevState.filter(call => call.roomId !== roomId)
          );
        });

        // Adicionando os listeners de WebRTC aqui

        currentSocket.on('offer', async (offer, buyerSocketData) => {
          if (offer) {
            setRemoteUserId(buyerSocketData.userId);

            console.log('Definindo descrição remota (oferta)...');
            await peerConnection.current?.setRemoteDescription(offer);
            console.log('Descrição remota definida');

            await addPendingCandidates();

            console.log('Criando resposta...');
            const answer = await peerConnection.current?.createAnswer({
              offerToReceiveAudio: true,
              offerToReceiveVideo: true,
            });
            console.log('Resposta criada:', answer?.type);

            console.log('Definindo descrição local (resposta)...');
            await peerConnection.current?.setLocalDescription(answer);
            console.log('Descrição local definida');

            currentSocket?.emit('answer', answer, buyerSocketData.userId);
            setBuyerInfo(buyerSocketData);
          }
        });

        currentSocket.on('ice-candidate', async candidate => {
          try {
            if (peerConnection.current?.remoteDescription) {
              console.log('Adicionando candidato ICE...');
              await peerConnection.current.addIceCandidate(candidate);
              console.log('Candidato ICE adicionado com sucesso');
            } else {
              console.log('Armazenando candidato ICE pendente');
              pendingCandidates.current.push(candidate);
            }
          } catch (error) {
            console.error('Erro ao adicionar candidato ICE:', error);
            pendingCandidates.current.push(candidate);
          }
        });

        currentSocket.on('user-disconnected', () => {
          setRemoteUserId(null);
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = null;
          }
        });

        currentSocket.on('connect_error', err => {
          console.error('Erro de conexão:', err);
          setError(
            'Erro ao conectar ao servidor. Verifique se o servidor está rodando.'
          );
          setConnected(false);
        });

        currentSocket.on('disconnect', () => {
          console.log('Desconectado do servidor Socket.IO');
          setConnected(false);

          // Limpando chamadas pendentes e parando o som
          setCallList([]);
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }

          // Fechando o modal e limpando estados quando perder conexão
          if (isOpenCallDialog) {
            endCall(true);
          }
        });
        break;
      }
      case 'disconnect':
        if (socket) {
          // Remover todos os listeners antes de desconectar
          socket.off('offer');
          socket.off('ice-candidate');
          socket.off('buyer-request-call');
          socket.off('buyer-reject-call');
          socket.off('call-attended');
          socket.off('connect_error');
          socket.off('disconnect');

          socket.disconnect();
          setSocket(null);
          setConnected(false);
          // Limpar todas as chamadas pendentes ao desconectar
          setCallList([]);
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
          }
        }
        break;
      default:
        console.log('Ação inválida');
    }
  };

  useEffect(() => {
    return toggleConnection('disconnect');
  }, [hiddeMenuAndHeaderItens]);

  // Expandir StatusContainer automaticamente quando há chamadas
  useEffect(() => {
    if (callList.length > 0 && isStatusMinimized) {
      setIsStatusMinimized(false);
      setShowCallList(true);
    }
  }, [callList, isStatusMinimized]);

  // Minimizar automaticamente quando não há chamadas (após 5 segundos)
  useEffect(() => {
    if (callList.length === 0 && !isStatusMinimized) {
      const timer = setTimeout(() => {
        setIsStatusMinimized(true);
        setShowCallList(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [callList, isStatusMinimized]);

  // Controlar visibilidade do StatusCard
  useEffect(() => {
    setShowCallList(!isStatusMinimized);
  }, [isStatusMinimized]);

  // Efeito para monitorar o estado da stream remota
  useEffect(() => {
    const checkRemoteStream = () => {
      if (remoteVideoRef.current && remoteVideoRef.current.srcObject) {
        const stream = remoteVideoRef.current.srcObject as MediaStream;
        const tracks = stream.getTracks();

        console.log('Status da stream remota:', {
          hasStream: !!stream,
          trackCount: tracks.length,
          audioTracks: tracks.filter(t => t.kind === 'audio').length,
          videoTracks: tracks.filter(t => t.kind === 'video').length,
          tracksEnabled: tracks.filter(t => t.enabled).length,
          videoReadyState: remoteVideoRef.current?.readyState,
          videoPaused: remoteVideoRef.current?.paused,
          videoCurrentTime: remoteVideoRef.current?.currentTime,
        });

        // Se há tracks mas o vídeo não está reproduzindo, tenta forçar
        if (tracks.length > 0 && remoteVideoRef.current?.paused) {
          console.log('Tentando forçar reprodução do vídeo remoto');

          remoteVideoRef.current.play().catch(error => {
            console.error('Erro ao forçar reprodução:', error);
          });
        }
      }
    };

    // Verifica a cada 2 segundos
    const interval = setInterval(checkRemoteStream, 2000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <div>
      {isOpenCallDialog && (
        <Draggable
          disabled={!isMinimized}
          position={isMinimized ? dragPosition : { x: 0, y: 0 }}
          onStop={(_, data) => setDragPosition({ x: data.x, y: data.y })}
        >
          <VideoCallContainer minimized={isMinimized}>
            <VideoCallContent minimized={isMinimized}>
              {isMinimized ? (
                <RestoreBtn onClick={handleRestore} title="Restaurar">
                  <FullscreenOutlinedIcon />
                </RestoreBtn>
              ) : (
                <MinimizeBtn
                  onClick={() => setIsMinimized(true)}
                  title="Minimizar"
                >
                  <FullscreenExitOutlinedIcon fontSize="large" />
                </MinimizeBtn>
              )}
              <VideoRoomContainer minimized={isMinimized}>
                {error && showError && (
                  <ErrorMessage>
                    <span>{error}</span>
                    <CloseButton onClick={handleCloseError} title="Fechar">
                      <CloseIcon fontSize="small" />
                    </CloseButton>
                  </ErrorMessage>
                )}
                <VideoContainer minimized={isMinimized}>
                  <VideoWrapper minimized={isMinimized}>
                    <Video
                      ref={remoteVideoRef}
                      autoPlay
                      playsInline
                      className="video remote-video"
                      minimized={isMinimized}
                    />
                    <VideoLabel minimized={isMinimized}>
                      {remoteUserId
                        ? buyerInfos?.userName
                        : 'Aguardando conexão...'}
                    </VideoLabel>
                  </VideoWrapper>
                  <LocalVideoMini
                    style={{ display: isMinimized ? 'none' : undefined }}
                  >
                    <LocalVideo
                      ref={localVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className="video local-video"
                    />
                    <VideoLabelMini>Você</VideoLabelMini>
                  </LocalVideoMini>
                </VideoContainer>
                {!isMinimized && (
                  <CameraControls>
                    <CameraControlButton
                      onClick={toggleMic}
                      className={`toggle-mic ${!isMicOn ? 'off' : ''}`}
                      title={isMicOn ? 'Mutar' : 'Desmutar'}
                    >
                      {isMicOn ? <MicIcon /> : <MicOffIcon />}
                    </CameraControlButton>
                    <CameraControlButton
                      onClick={toggleCamera}
                      className={`toggle-camera ${!isCameraOn ? 'off' : ''}`}
                      title={isCameraOn ? 'Desativar Câmera' : 'Ativar Câmera'}
                    >
                      {isCameraOn ? <VideocamIcon /> : <VideocamOffIcon />}
                    </CameraControlButton>
                    <CameraControlButton
                      onClick={toggleScreenShare}
                      className={`toggle-screen ${
                        isScreenSharing ? 'active' : ''
                      }`}
                      title={
                        isScreenSharing
                          ? 'Parar Compartilhamento'
                          : 'Compartilhar Tela'
                      }
                    >
                      {isScreenSharing ? (
                        <StopScreenShareIcon />
                      ) : (
                        <ScreenShareIcon />
                      )}
                    </CameraControlButton>
                    <CameraControlButton
                      onClick={() => endCall(false)}
                      className={'end-call'}
                      title={'Encerrar Chamada'}
                    >
                      <CallEndIcon style={{ color: '#000' }} />
                    </CameraControlButton>
                  </CameraControls>
                )}
              </VideoRoomContainer>
            </VideoCallContent>
          </VideoCallContainer>
        </Draggable>
      )}

      {/* StatusContainer com condições originais */}
      {(() => {
        const shouldRender =
          hasProduct('VIDEO_CALL') &&
          hasPermission('VIDEO_CALL_ANSWER') &&
          !hiddeMenuAndHeaderItens;

        return shouldRender ? (
          <StatusContainer minimized={isStatusMinimized}>
            <StatusCard style={{ display: showCallList ? 'flex' : 'none' }}>
              <StatusContent>
                <StatusInfo>
                  <StatusIcon>
                    <HeadsetIcon />
                  </StatusIcon>
                  <StatusText>
                    {connected
                      ? 'Disponível para chamadas'
                      : 'Indisponível para chamadas'}
                  </StatusText>
                </StatusInfo>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Switch
                    checked={connected}
                    onChange={(): void =>
                      toggleConnection(connected ? 'disconnect' : 'connect')
                    }
                    color="primary"
                    disabled={isOpenCallDialog}
                  />
                  <button
                    onClick={() => {
                      setIsStatusMinimized(!isStatusMinimized);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title={isStatusMinimized ? 'Expandir' : 'Minimizar'}
                  >
                    {isStatusMinimized ? (
                      <KeyboardArrowUpIcon fontSize="small" />
                    ) : (
                      <KeyboardArrowDownIcon fontSize="small" />
                    )}
                  </button>
                </div>
              </StatusContent>
            </StatusCard>

            {/* Componente minimizado */}
            {!showCallList && (
              <div
                style={{
                  position: 'fixed',
                  bottom: '15px',
                  right: '30px',
                  width: '50px',
                  height: '50px',
                  background: '#fff',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  zIndex: 2000,
                  border: '2px solid #fff',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                }}
                onClick={() => {
                  setIsStatusMinimized(false);
                  setShowCallList(true);
                }}
                title="Expandir painel de chamadas"
              >
                <HeadsetIcon style={{ color: 'white', fontSize: '24px' }} />
                {/* Badge de notificação */}
                {callList.length > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '-5px',
                      right: '-5px',
                      background: '#ff4444',
                      color: 'white',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      animation: 'pulse 2s infinite',
                    }}
                    title={`${callList.length} chamada(s) pendente(s)`}
                  >
                    {callList.length}
                  </div>
                )}
                {/* Bolinha de status da conexão */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '-3px',
                    right: '-3px',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: connected ? '#4CAF50' : '#9E9E9E',
                    border: '2px solid #fff',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                  }}
                  title={connected ? 'Conectado' : 'Desconectado'}
                />
              </div>
            )}

            <CallListContainer className="call-list-container">
              {callList.map((callData, index) => {
                return (
                  <CallCard key={index}>
                    <CallInfo>
                      <CallUserName>{callData.userName}</CallUserName>
                      <CallDealContainer>
                        <DealLink
                          href={callData.dealLink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {callData.dealName}
                        </DealLink>
                      </CallDealContainer>
                    </CallInfo>
                    <CallActions>
                      <AcceptButton
                        onClick={() => {
                          startCall(callData, socket);
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        <CallIcon fontSize={'small'} />
                      </AcceptButton>
                      <RejectButton onClick={() => rejectCall(callData)}>
                        <CallEndIcon fontSize={'small'} />
                      </RejectButton>
                    </CallActions>
                  </CallCard>
                );
              })}
            </CallListContainer>
            {/* Notificação removida - agora está no componente minimizado */}
          </StatusContainer>
        ) : null;
      })()}
    </div>
  );
};

export default VideoCallProvider;
